/**
 * 줄서기 화면 공통 조각 — AppBar / 언어 선택 / 정보 행 / 강조 문장 / 경고 배너.
 *
 * @see #781 디자인 원본 app.jsx (AppBar, LangSelect, Row)
 */
import { useState } from 'react';
import { ChevronDown, ExternalLink, Globe, TriangleAlert, Utensils } from 'lucide-react';
import { SUPPORTED_LOCALES, type Locale } from '@/config/locales';
import type { WaitlistSegment } from './strings';
import { safeExternalUrl } from './utils';

/**
 * 강조 구간이 섞인 문장 렌더.
 *
 * `bold` 는 주변 색을 상속한 굵은 글씨(디자인 `B`), `strong` 은 본문색 강조(디자인 `Bf`).
 */
export function WaitlistSegments({ segments }: { segments: WaitlistSegment[] }) {
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.bold) {
          return <b key={index}>{segment.text}</b>;
        }
        if (segment.strong) {
          return (
            <b key={index} className="text-foreground">
              {segment.text}
            </b>
          );
        }
        return <span key={index}>{segment.text}</span>;
      })}
    </>
  );
}

/**
 * 언어 선택 — 15개 언어 (config/locales SUPPORTED_LOCALES 를 SSOT 로 사용).
 *
 * 네이티브 `<select>` 를 투명하게 겹쳐 모바일 기본 피커를 그대로 쓴다(디자인 원본과 동일).
 * `dir="ltr"` 고정 — 아랍어 선택 시에도 국기/언어명 배치가 뒤집히지 않게 한다.
 */
export function WaitlistLangSelect({
  locale,
  onLocaleChange,
  label,
}: {
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  label: string;
}) {
  const current = SUPPORTED_LOCALES.find((l) => l.code === locale) ?? SUPPORTED_LOCALES[0];

  return (
    <label
      dir="ltr"
      aria-label={label}
      className="relative inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-background-secondary py-[5px] pl-[11px] pr-2.5 text-xs font-bold text-foreground"
    >
      <Globe className="size-[15px]" />
      <span className="pointer-events-none">{current.nativeName}</span>
      <ChevronDown className="size-3 opacity-50" />
      <select
        value={locale}
        onChange={(event) => onLocaleChange(event.target.value as Locale)}
        aria-label={label}
        /* fontSize 16px — iOS Safari 가 그 미만이면 포커스 시 페이지를 확대한다. */
        className="absolute inset-0 size-full cursor-pointer text-[16px] opacity-0"
      >
        {SUPPORTED_LOCALES.map((option) => (
          <option key={option.code} value={option.code}>
            {option.nativeName}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * 매장명 + 부제 + 언어 선택 헤더.
 *
 * `logoUrl` 이 있으면 아이콘 대신 매장 로고 이미지를 표시하고, 로드에 실패하면
 * (깨진 링크 등) 아이콘으로 폴백한다. `mapsUrl` 이 있으면 부제를 새 탭 위치 링크로
 * 바꾼다(#793).
 */
export function WaitlistAppBar({
  storeName,
  subtitle,
  locale,
  onLocaleChange,
  languageLabel,
  logoUrl = null,
  mapsUrl = null,
}: {
  storeName: string;
  subtitle: string;
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  languageLabel: string;
  logoUrl?: string | null;
  mapsUrl?: string | null;
}) {
  const [logoFailed, setLogoFailed] = useState(false);
  // 서버가 내려주는 URL 은 신뢰할 수 없는 입력이다 — javascript:/data: 스킴이나
  // 상대 경로가 섞이면 렌더 자체를 막는다(#794 Task 9 코드리뷰).
  const safeLogoUrl = safeExternalUrl(logoUrl);
  const safeMapsUrl = safeExternalUrl(mapsUrl);
  const showLogo = Boolean(safeLogoUrl) && !logoFailed;

  return (
    <div
      data-testid="waitlist-app-bar"
      className="flex shrink-0 items-center gap-3 px-5 pb-3 pt-1.5"
    >
      <div className="grid size-[42px] shrink-0 place-items-center overflow-hidden rounded-[13px] bg-primary text-primary-foreground">
        {showLogo && safeLogoUrl ? (
          <img
            src={safeLogoUrl}
            alt=""
            className="size-full object-cover"
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <Utensils className="size-[22px]" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-bold leading-tight">{storeName}</div>
        {safeMapsUrl ? (
          <button
            type="button"
            onClick={() => window.open(safeMapsUrl, '_blank', 'noopener,noreferrer')}
            className="inline-flex max-w-full items-center gap-1 bg-transparent p-0 text-xs text-muted-foreground underline underline-offset-2"
          >
            <span className="truncate">{subtitle}</span>
            <ExternalLink className="size-3 shrink-0" />
          </button>
        ) : (
          <div className="truncate text-xs text-muted-foreground">{subtitle}</div>
        )}
      </div>
      <WaitlistLangSelect locale={locale} onLocaleChange={onLocaleChange} label={languageLabel} />
    </div>
  );
}

/** 아이콘 · 라벨 · 값 정보 행. */
export function WaitlistRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-muted-foreground">{icon}</span>
      <span className="flex-1 text-[13px] text-muted-foreground">{label}</span>
      <span className="text-[13px] font-semibold">{value}</span>
    </div>
  );
}

/** 파괴적/주의 안내 배너 (인원 초과, GPS 거리 등). */
export function WaitlistWarning({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-2 rounded-md bg-destructive/[0.13] px-3 py-2.5 text-[12.5px] leading-snug text-destructive-dark">
      <span className="mt-px shrink-0">
        <TriangleAlert className="size-4" />
      </span>
      <span>{children}</span>
    </div>
  );
}
